import { AfterViewInit, Component, Input, OnInit, ViewChild, Output, EventEmitter } from '@angular/core';
import { DxDataGridComponent } from "devextreme-angular";
import { PortalTemplateService } from "src/app/modules/admin/_api/services/portal-template.service";
import { TimeService } from "src/app/modules/time/_api/services/time.service";
import { AmsNotifierService } from "src/app/shared/services/ams-notifier.service";
import { LanguageService } from "src/app/shared/services/language.service";
import { UserService } from "src/app/shared/services/user.service";
import { GridToQuickEntryConverter } from "./grid-to-quick-entry-converter.service";

@Component({
  selector: 'app-quick-entry',
  templateUrl: './quick-entry.component.html',
  styleUrls: ['./quick-entry.component.css']
})
export class QuickEntryComponent implements OnInit, AfterViewInit {
  @Input() employees = [];
  @Input() projects: any;
  @Output() saved = new EventEmitter<Date>();
  
  dataSource = [];
  columns = [];
  equipment = [];
  timeTypeColumns = [];
  isBusy: boolean;

  @ViewChild(DxDataGridComponent, {static: false}) dataGrid: DxDataGridComponent;

  constructor(
    public languageService: LanguageService,
    private timeService: TimeService,
    private portalTemplateService: PortalTemplateService,
    private amsNotifier: AmsNotifierService,
    private userService: UserService,
    private gridToQuickEntryConverter: GridToQuickEntryConverter) {}

  async ngOnInit() {    
    this.setColumns();
    this.equipment = (await this.timeService.getEquipment().toPromise()).Data;
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.addLoggedInUserToEmployeeListWhenNotExists();
      this.dataGrid.instance.addRow();
    }, 500);
  }

  private addLoggedInUserToEmployeeListWhenNotExists() {
    const doesMyUserExistInEmployeeList = this.employees.filter(e => e.EmployeeId === this.userService.account.EmployeeId).length > 0;

    if (doesMyUserExistInEmployeeList) return;    
    this.employees.push({ EmployeeId: this.userService.account.EmployeeId, EmployeeNumberAndName: this.userService.account.Name });
  }

  async setColumns() {
    const columnSettings = await this.portalTemplateService.getQuickEntryGridColumn().toPromise();
    
    this.columns = [
      { dataField: "Date", caption: this.languageService.LNG("Label.Date"), visible: columnSettings.ShowDate, dataType: "date", format: { day: '2-digit', month: '2-digit', year: '2-digit' }, width: 150, fixed: "left", validationRules: [{ type: "required" }] },
      { dataField: "EmployeeNumberAndName", caption: this.languageService.LNG("Label.Employee"), editCellTemplate: "employeeEditTemplate", setCellValue: this.setEmployeeValue, visible: columnSettings.ShowEmployee, fixed: "left", alignment: "left", validationRules: [{ type: "required" }] },
      { dataField: "Project", caption: this.languageService.LNG("Label.Project"), editCellTemplate: "projectEditTemplate", setCellValue: this.setProjectValue, visible: columnSettings.ShowProject, fixed: "left", alignment: "left", validationRules: [{ type: "required" }] },
      { dataField: "Machine1", caption: `${this.languageService.LNG("Label.Machine")} 1`, editCellTemplate: "machine1EditTemplate", setCellValue: this.setMachine1Value, visible: columnSettings.ShowMachine1, width: 450, fixed: "left", alignment: "left" },
      { dataField: "Machine2", caption: `${this.languageService.LNG("Label.Machine")} 2`, editCellTemplate: "machine2EditTemplate", setCellValue: this.setMachine2Value, visible: columnSettings.ShowMachine2, width: 450, fixed: "left", alignment: "left" }
    ];

    this.timeTypeColumns = columnSettings.TimeTypeColumnDtos;
    this.timeTypeColumns.forEach((timeTypeColumnDto: any) => {
      this.columns.push({ dataField: `TimeType_${timeTypeColumnDto.TimeTypeId}`, caption: timeTypeColumnDto.Code, alignment: "right", cellTemplate: "timeCellTemplate", editCellTemplate: "timeEditTemplate", allowSorting: false, allowFiltering: false, width: 150 });
    });
  }

  async onSaving(e: any) {
    e.cancel = true;

    const convertedResult = this.gridToQuickEntryConverter.convert(e.changes);

    if (!convertedResult.isValid) { return; }

    this.isBusy = true;
    const result = await this.timeService.saveQuickEntryTimes(convertedResult.quickEntries).toPromise();
    this.isBusy = false;

    if (!result.IsSuccess) {
      this.amsNotifier.error(result.ErrorMessage);
      return;
    }

    const timestamp = result.Data;

    this.amsNotifier.success(this.languageService.LNG("Label.SavedOK"));
    this.saved.emit(timestamp);
  }

  onInitNewRow(e: any) {
    const doesMyUserExistInEmployeeList = this.employees.filter(e => e.EmployeeId === this.userService.account.EmployeeId).length > 0;
    
    if (!doesMyUserExistInEmployeeList) {
      this.employees.push({ EmployeeId: this.userService.account.EmployeeId, EmployeeNumberAndName: this.userService.account.Name });
    }
    
    e.data.Date = new Date();
    e.data.EmployeeId = this.userService.account.EmployeeId;
    e.data.EmployeeNumberAndName = this.userService.account.Name;
  }
  
  onEmployeeValueChanged(e: any, cell: any) {
    const selectedEmployee = e.component.option("selectedItem");    

    if (selectedEmployee) {
      cell.setValue(selectedEmployee);
    }
  }

  private setEmployeeValue(newData: any, value: any) {
    newData.EmployeeId = value && value.EmployeeId;
    newData.EmployeeNumberAndName = value && value.EmployeeNumberAndName;
  }

  private setProjectValue(newData: any, value: any) {
    newData.CarrierProjectId = value && value.ProjectId;
    newData.Project = value && value.ProjectDisplayName;
    newData.CustomerId = value && value.CustomerId;
    newData.CarrierId = value && value.CarrierId;
  }

  onProjectValueChanged(e: any, cell: any) {
    const selectedProject = e.component.option("selectedItem");

    if (selectedProject) {
      cell.setValue(selectedProject);
    }
  }

  onMachineValueChanged(e: any, cell: any) {
    const selectedMachine = e.component.option("selectedItem");

    if (selectedMachine) {
      cell.setValue(selectedMachine);
    }
  }

  onTimeValueChanged(value: any, cell: any) {
    cell.setValue(value);
  }

  private setMachine1Value(newData: any, value: any) {
    newData.EquipmentId1 = value && value.EquipmentId;
    newData.Machine1 = value && value.DisplayName;
  }

  private setMachine2Value(newData: any, value: any) {
    newData.EquipmentId2 = value && value.EquipmentId;
    newData.Machine2 = value && value.DisplayName;
  }
}
